import { useMemo, useState, type ReactNode } from 'react';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { createQueryClient } from './queryClient';
import { createAppTheme } from '@/theme/theme';
import { useUiStore } from '@/store/uiStore';
import { ConfirmProvider } from '@/providers/ConfirmProvider';

/** Global providers: React Query, MUI theme (reacts to prefs), and snackbars. */
export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => createQueryClient());
  const mode = useUiStore((s) => s.mode);
  const density = useUiStore((s) => s.density);
  const theme = useMemo(() => createAppTheme(mode, density), [mode, density]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            '#root': { minHeight: '100vh' },
            '*::-webkit-scrollbar': { width: 10, height: 10 },
            '*::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.divider,
              borderRadius: 8,
            },
          }}
        />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          autoHideDuration={4000}
        >
          <ConfirmProvider>{children}</ConfirmProvider>
        </SnackbarProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  );
}
